import React from 'react';
import { Bot, CalendarCheck, Package, DollarSign, Wallet, Users, Zap, ArrowRight, CheckCircle, Shield } from 'lucide-react';

export default function SolutionsPage({ module }) {
  const solutionsData = {
    agenda: {
      title: 'Agenda Inteligente & Automatización de Citas',
      subtitle: 'Olvídate de responder chats manualmente y llenar libretas. Keyla IA agenda 24/7 por ti.',
      icon: <CalendarCheck size={40} className="text-seryi-green" />,
      badge: 'MÓDULO DE RESERVAS',
      heroDesc: 'Tu asistente IA interactúa en WhatsApp en lenguaje natural, sugiere horarios disponibles, agenda citas, envía recordatorios automáticos y reduce el ausentismo en más de un 80%.',
      features: [
        'Sincronización instantánea con Google Calendar',
        'Recordatorios de WhatsApp 24h y 2h antes de cada cita',
        'Asignación inteligente por especialidad del profesional',
        'Página pública personalizada (/app/tu-negocio)',
        'Cero dobles reservas ni cruces de horarios'
      ],
      ctaText: 'Activar Agenda IA',
      color: 'from-emerald-500/20 to-teal-500/10'
    },
    inventario: {
      title: 'Control de Inventario & Almacén en Tiempo Real',
      subtitle: 'Administra tus insumos, productos y alertas de bajo stock de forma automática.',
      icon: <Package size={40} className="text-seryi-green" />,
      badge: 'MÓDULO DE INVENTARIO',
      heroDesc: 'Evita pérdidas y quiebres de inventario con un sistema inteligente que descuenta stock por cada venta o servicio realizado y te avisa cuando un producto está por agotarse.',
      features: [
        'Descuento automático por insumos utilizados en servicios',
        'Alertas automáticas por bajo stock por WhatsApp',
        'Control de costos y valoración de inventario',
        'Soporte multidispositivo y sucursales en tiempo real',
        'Historial de auditoría y movimientos de mercancía'
      ],
      ctaText: 'Controlar Inventario',
      color: 'from-blue-500/20 to-cyan-500/10'
    },
    caja: {
      title: 'Sistema de Caja, Facturación POS & Finanzas',
      subtitle: 'Cierre de caja en 1 clic, control de ingresos y reportes financieros sin errores matemáticos.',
      icon: <DollarSign size={40} className="text-seryi-green" />,
      badge: 'MÓDULO DE FINANZAS',
      heroDesc: 'Registra cobros en efectivo, Nequi, Daviplata, tarjeta o transferencia con comprobante digital, visualiza tu flujo de efectivo en tiempo real y realiza cierres automáticos sin estrés.',
      features: [
        'Apertura y cierre de caja rápido e intuitivo',
        'Múltiples métodos de pago integrados (Nequi/Daviplata/Efectivo)',
        'Generación de recibos y facturación por WhatsApp',
        'Reporte de ganancias y gastos por día, semana y mes',
        'Auditoría total contra cuadre físico de dinero'
      ],
      ctaText: 'Optimizar Mi Caja',
      color: 'from-amber-500/20 to-yellow-500/10'
    },
    wallet: {
      title: 'Wallet & Pagos Digitales Integrados',
      subtitle: 'Recibe pagos en segundos con códigos QR dinámicos y conciliación automática.',
      icon: <Wallet size={40} className="text-seryi-green" />,
      badge: 'MÓDULO DE PAGOS',
      heroDesc: 'Tu negocio moderno conectado a transferencias digitales, verificación por inteligencia artificial de capturas de comprobantes y saldo consolidado al instante.',
      features: [
        'Integración con Nequi, Daviplata y transferencias bancarias',
        'Reconocimiento OCR con IA para verificar comprobantes de pago',
        'Saldos acumulados y control de retiros',
        'Cero comisiones ocultas por transacciones manuales',
        'Link de cobro seguro vía WhatsApp'
      ],
      ctaText: 'Activar Wallet Digital',
      color: 'from-purple-500/20 to-indigo-500/10'
    },
    comisiones: {
      title: 'Cálculo de Comisiones a Profesionales',
      subtitle: 'Cálculo transparente, rápido y sin dolores de cabeza para tus barberos, esteticistas y staff.',
      icon: <Users size={40} className="text-seryi-green" />,
      badge: 'MÓDULO DE EQUIPO',
      heroDesc: 'Parametriza comisiones por porcentaje o monto fijo según el servicio y profesional. Keyla calcula automáticamente el total a pagar al final del turno, semana o mes.',
      features: [
        'Comisiones configurables por profesional y por servicio',
        'Portal independiente para que cada empleado vea sus ganancias',
        'Descuentos y anticipos automáticos en nómina',
        'Reporte de rendimiento y productividad por colaborador',
        'Liquidación con un solo clic exportable'
      ],
      ctaText: 'Automatizar Comisiones',
      color: 'from-rose-500/20 to-pink-500/10'
    },
    flex: {
      title: 'Keyla Flex & Add-ons Corporativos',
      subtitle: 'Módulos escalables a la medida de tu industria: monitor de cocina, carta digital y más.',
      icon: <Zap size={40} className="text-seryi-green" />,
      badge: 'MÓDULO DE EXTENSIONES',
      heroDesc: 'Expande Keyla CRM con extensiones especializadas para tu nicho: menús QR para restaurantes, monitor de cocina (KDS), domicilios por WhatsApp e integraciones nativas.',
      features: [
        'Carta digital y pedidos en vivo para restaurantes y cafeterías',
        'Monitor de Cocina KDS con cronómetro de preparación',
        'Portal de repartidores y domicilios por WhatsApp',
        'Conexión API y webhooks nativos',
        'Soporte corporativo y personalizaciones a medida'
      ],
      ctaText: 'Explorar Keyla Flex',
      color: 'from-seryi-green/20 to-emerald-500/10'
    }
  };

  const current = solutionsData[module] || solutionsData.agenda;

  return (
    <div className="min-h-screen bg-seryi-bg-dark text-seryi-text font-sans">
      {/* NAVBAR */}
      <nav className="border-b border-white/10 bg-seryi-bg-dark/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-seryi-green to-[#00cc7d] flex items-center justify-center shadow-lg shadow-seryi-green/20">
              <Bot className="text-seryi-bg-dark" size={24} />
            </div>
            <span className="text-xl font-black text-white tracking-tight">KEYLA<span className="text-seryi-green">CRM</span></span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/planes" className="text-sm font-semibold text-white hover:text-seryi-green transition-colors">
              Planes y Precios
            </a>
            <a href="/signup" className="px-5 py-2.5 rounded-xl bg-seryi-green text-seryi-bg-dark font-extrabold text-sm hover:brightness-110 transition-all shadow-lg shadow-seryi-green/20">
              Empezar Gratis
            </a>
          </div>
        </div>
      </nav>

      {/* HERO SECTION */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-seryi-green text-xs font-extrabold uppercase tracking-wider mb-6">
            {current.badge}
          </div>
          <div className="flex justify-center mb-6">
            <div className="p-4 rounded-3xl bg-white/5 border border-white/10 shadow-2xl">
              {current.icon}
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight mb-6">
            {current.title}
          </h1>
          <p className="text-xl text-seryi-text font-medium mb-8">
            {current.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/signup"
              className="w-full sm:w-auto px-8 py-4 rounded-2xl bg-seryi-green text-seryi-bg-dark font-black text-base hover:brightness-110 transition-all shadow-xl shadow-seryi-green/20 flex items-center justify-center gap-2"
            >
              {current.ctaText}
              <ArrowRight size={18} />
            </a>
            <a
              href="/planes"
              className="w-full sm:w-auto px-8 py-4 rounded-2xl bg-white/10 text-white font-bold text-base hover:bg-white/20 transition-all flex items-center justify-center"
            >
              Ver Planes y Límites
            </a>
          </div>
        </div>

        {/* DETAILS GRID */}
        <div className={`rounded-3xl p-8 sm:p-12 bg-gradient-to-br ${current.color} border border-white/10 shadow-2xl max-w-5xl mx-auto`}>
          <h2 className="text-2xl font-black text-white mb-4">¿Cómo funciona para tu negocio?</h2>
          <p className="text-seryi-text text-base mb-8 leading-relaxed">
            {current.heroDesc}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {current.features.map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-seryi-bg-dark/60 p-4 rounded-2xl border border-white/5">
                <CheckCircle size={20} className="text-seryi-green flex-shrink-0 mt-0.5" />
                <span className="text-sm font-semibold text-white">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
